import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DiscontinuedServiceChip from './discontinued-service-chip.component';
import { DISCONTINUED_TOKENS } from './discontinued-service.tokens';

/**
 * Yellow appointment-card wrapper used on schedule-calendar event cells when the appointment
 * is tied to a soft-deleted service. Maps to the four design variants documented in
 * /home/dev/.claude/artifacts/CIN-5774/figma-brief.md §2.
 *
 *   variant="single"   — full-size day-view card with a single discontinued service.
 *   variant="multiple" — full-size day-view card showing a count (e.g. "5 services").
 *   variant="medium"   — narrow week-view column card; no top stripe, no bottom warning row.
 *   variant="small"    — tightest variant (month chip / inline cells); icon-only chip.
 *
 * Color tokens come from `discontinued-service.tokens.js` — Figma `light/yellow/200|600|700`.
 *
 * TODO design QA: see tokens file for the long-term plan to migrate to a theme token.
 */
export const DiscontinuedServiceCard = ({
  variant = 'single',
  customerName,
  serviceLabel,
  discontinuedCount = 1,
  totalServiceCount,
  children
}) => {
  if (variant === 'small') {
    return <Box data-testid="discontinued-service-card-small" sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      height: 36,
      width: '100%',
      minWidth: 0,
      px: 1,
      backgroundColor: DISCONTINUED_TOKENS.bg,
      border: `1px solid ${DISCONTINUED_TOKENS.accent}`,
      borderRadius: 1
    }}>
        <Typography noWrap sx={{
        fontSize: 12,
        fontWeight: 500,
        color: 'text.primary',
        flex: 1,
        minWidth: 0
      }}>
          {customerName || 'Customer'}
        </Typography>
        <DiscontinuedServiceChip size="sm" />
      </Box>;
  }
  const showBottomWarningRow = variant !== 'medium';
  const showTopStripe = variant === 'single' || variant === 'multiple';
  return <Box data-testid={`discontinued-service-card-${variant}`} sx={{
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: variant === 'medium' ? 72 : 96,
    width: '100%',
    // 8px outer radius (Figma); the stripe + body share the rounded corners.
    borderRadius: '8px',
    overflow: 'hidden'
  }}>
      {/* 4px top status stripe — Figma `light/yellow/600` */}
      {showTopStripe && <Box aria-hidden sx={{
      height: 4,
      width: '100%',
      backgroundColor: DISCONTINUED_TOKENS.accent,
      borderRadius: '8px 8px 0 0',
      flexShrink: 0
    }} />}

      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      p: 1.25,
      gap: 0.5,
      backgroundColor: DISCONTINUED_TOKENS.bg,
      border: `1px solid ${DISCONTINUED_TOKENS.accent}`,
      // Bottom corners only when the stripe is on top; otherwise full radius.
      borderRadius: showTopStripe ? '0 0 8px 8px' : '8px',
      // Avoid double border under the stripe.
      ...(showTopStripe && {
        borderTop: 'none'
      })
    }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Typography sx={{
          fontWeight: 700,
          fontSize: 14,
          color: 'text.primary',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
            {customerName || 'Customer'}
          </Typography>
          <DiscontinuedServiceChip size="md" />
        </Stack>

        {serviceLabel && <Typography sx={{
        fontSize: 12,
        color: 'text.secondary',
        textDecoration: variant === 'single' ? 'line-through' : 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
            {variant === 'multiple' && totalServiceCount ? `${totalServiceCount} services` : serviceLabel}
          </Typography>}

        {children}

        {showBottomWarningRow && <Stack direction="row" alignItems="center" spacing={0.5} sx={{
        mt: 'auto'
      }}>
            <ReportProblemOutlinedIcon sx={{
          fontSize: 24,
          color: DISCONTINUED_TOKENS.deep
        }} />
            <Typography sx={{
          // typography/body/md — 16px / 24 line-height / 500 / 0.15 tracking
          fontSize: 16,
          lineHeight: '24px',
          fontWeight: 500,
          letterSpacing: 0.15,
          color: DISCONTINUED_TOKENS.deep
        }}>
              {discontinuedCount} {discontinuedCount === 1 ? 'service discontinued' : 'services discontinued'}
            </Typography>
          </Stack>}
      </Box>
    </Box>;
};
DiscontinuedServiceCard.propTypes = {
  variant: PropTypes.oneOf(['single', 'multiple', 'medium', 'small']),
  customerName: PropTypes.string,
  serviceLabel: PropTypes.string,
  discontinuedCount: PropTypes.number,
  totalServiceCount: PropTypes.number,
  children: PropTypes.node
};
export default DiscontinuedServiceCard;